import { useState, useCallback } from 'react';
import { ModalProps } from '../components/Modals/Modal/Modal';

export interface UseModalOptions {
  isUnclosable?: boolean;
  windowClassName?: string;
  variant?: 'default' | 'app';
}

export interface UseModalReturn {
  isOpen: boolean;
  isLoading: boolean;
  open: (content?: React.ReactNode, options?: UseModalOptions) => void;
  close: () => void;
  setIsLoading: (isLoading: boolean) => void;
  content: React.ReactNode;
  /** Props to spread on Modal: isOpen, onClose, content, isUnclosable */
  modalProps: ModalProps;
}

export const useModal = (
  initialContent: React.ReactNode = null
): UseModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState<React.ReactNode>(initialContent);
  const [isUnclosable, setIsUnclosable] = useState<boolean | undefined>(
    undefined
  );
  const [windowClassName, setWindowClassName] = useState<string | undefined>(
    undefined
  );
  const [variant, setVariant] = useState<'default' | 'app' | undefined>(
    undefined
  );

  const open = useCallback(
    (newContent?: React.ReactNode, options?: UseModalOptions) => {
      if (newContent !== undefined) {
        setContent(newContent);
      }
      if (options?.isUnclosable !== undefined) {
        setIsUnclosable(options.isUnclosable);
      }
      setWindowClassName(options?.windowClassName);
      setVariant(options?.variant);
      setIsOpen(true);
    },
    []
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const modalProps = {
    isOpen,
    isLoading,
    onClose: close,
    content,
    isUnclosable,
    windowClassName,
    variant,
  };

  return {
    isOpen,
    isLoading,
    open,
    close,
    setIsLoading,
    content,
    modalProps,
  };
};
